import {Download, ListAlt, PowerSettingsNew, RestartAlt, Settings} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, CircularProgress, Menu, MenuItem, Typography} from '@mui/material';
import {FC, MouseEvent, useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {NavLink} from 'react-router-dom';
import {ocppApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {axiosInstance} from 'src/axiosBaseQuery';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {ResetTypeEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, ChargePointDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace, getBaseURL, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {
    deviceActionSx,
    qrConnectorBadgeSx,
    qrConnectorLabelSx,
    qrMenuItemSx,
    qrMenuPaperSx,
    qrMenuTitleSx,
} from './ChargerResetWrapper.sx';
import {ChargerResetWrapperType} from './ChargerResetWrapper.types';

type ResetTypeMutation = ReturnType<typeof ocppApi.useResetChargerMutation>[0];

const handleReset =
    (
        trigger: ResetTypeMutation,
        resetType: ResetTypeEnum,
        data: ChargePointDTO,
        dispatch: AppDispatch
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({businessId: data.business.id, chargerId: data.id, resetType});
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ChargerResetWrapper: FC<ChargerResetWrapperType> = ({data}) => {
    const [trigger] = ocppApi.useResetChargerMutation();
    const dispatch = useAppDispatch();
    // One permission per action, each the one its own endpoint requires. They used to share a single
    // gate - chargers.reset, on the block as a whole - so reading a log or a configuration was only
    // possible for somebody allowed to reboot the hardware, and chargerLogs.search on its own reached
    // nothing from this page.
    const canResetCharger = useCan('chargers.reset');
    const canReadQrCode = useCan('chargers.readQrCode');
    const canReadConfig = useCan('chargers.readConfig');
    const canReadChargerLogs = useCan('chargerLogs.search');
    const intl = useIntl();
    const [isQrCodeLoading, setIsQrCodeLoading] = useState(false);

    const [qrMenuAnchor, setQrMenuAnchor] = useState<HTMLElement | null>(null);

    // The QR is printed and glued to one physical socket, so what it carries is a connector, not a
    // charger. The backend holds connectors in an unordered Set and sorts them nowhere, so the order
    // they arrive in can differ from one load to the next - hence the sort here.
    const connectors = useMemo(
        () => [...(data.connectors ?? [])].sort((a, b) => a.connectorId - b.connectorId),
        [data.connectors]
    );

    const downloadQrCode = useCallback(async (connectorId: number) => {
        if (isQrCodeLoading) return;

        setIsQrCodeLoading(true);
        try {
            const response = await axiosInstance.get(
                `${getBaseURL()}/chargePoints/${data.id}/connectors/${connectorId}/qrcode`,
                {
                    params: {businessId: data.business.id},
                    responseType: 'blob',
                }
            );
            const blob = response.data as Blob;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `qr-code-${data.chargePointId}-connector-${connectorId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download QR code:', error);
            NotifyError(intl.formatMessage({id: 'qrCodeDownloadError'}));
        } finally {
            setIsQrCodeLoading(false);
        }
    }, [isQrCodeLoading, data.id, data.business.id, data.chargePointId, intl]);

    // One connector makes the question pointless, so the button answers it itself. More than one has
    // to be chosen: the code is glued to a socket, and the wrong sticker bills a guest for the car
    // in the next bay.
    const handleQrCodeClick = useCallback((event: MouseEvent<HTMLElement>) => {
        if (connectors.length === 1) {
            void downloadQrCode(connectors[0].connectorId);
            return;
        }
        setQrMenuAnchor(event.currentTarget);
    }, [connectors, downloadQrCode]);

    const handleQrMenuClose = useCallback(() => setQrMenuAnchor(null), []);

    const handleQrMenuSelect = useCallback((connectorId: number) => () => {
        setQrMenuAnchor(null);
        void downloadQrCode(connectorId);
    }, [downloadQrCode]);

    // Inside the device card now: no divider, no grid row of its own.
    // One row, one shape. Five actions that all do something to the device were split into two
    // groups with two different button styles - the resets in one, the links in another - so they
    // wrapped onto two lines and read as two unrelated sets.
    return (
        <>
            {canResetCharger && (
                <Button
                    sx={deviceActionSx}
                    onClick={handleReset(trigger, ResetTypeEnum.SOFT, data, dispatch)}
                    startIcon={<RestartAlt/>}
                >
                    <FormattedMessage id="softReset"/>
                </Button>
            )}
            {canResetCharger && (
                <Button
                    sx={deviceActionSx}
                    onClick={handleReset(trigger, ResetTypeEnum.HARD, data, dispatch)}
                    startIcon={<PowerSettingsNew/>}
                >
                    <FormattedMessage id="hardReset"/>
                </Button>
            )}
            {canReadQrCode && connectors.length > 0 && (
                <>
                    <Button
                        sx={deviceActionSx}
                        onClick={handleQrCodeClick}
                        disabled={isQrCodeLoading}
                        startIcon={isQrCodeLoading ? <CircularProgress size={16} color="inherit"/> : <Download/>}
                    >
                        <FormattedMessage id="qrCode"/>
                    </Button>
                    <Menu
                        anchorEl={qrMenuAnchor}
                        open={Boolean(qrMenuAnchor)}
                        onClose={handleQrMenuClose}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                        transformOrigin={{vertical: 'top', horizontal: 'center'}}
                        PaperProps={{sx: qrMenuPaperSx}}
                    >
                        <Typography sx={qrMenuTitleSx}>
                            <FormattedMessage id="qrCodeChooseConnector"/>
                        </Typography>
                        {connectors.map((connector) => (
                            <MenuItem
                                key={connector.id}
                                onClick={handleQrMenuSelect(connector.connectorId)}
                                sx={qrMenuItemSx}
                            >
                                <Box sx={qrConnectorBadgeSx}>{connector.connectorId}</Box>
                                <Typography sx={qrConnectorLabelSx}>
                                    <FormattedMessage id="connectorNumber" values={{number: connector.connectorId}}/>
                                </Typography>
                                <ColoredStatus label={connector.currentStatus} fontSize={11}/>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
            {canReadConfig && (
                <Button
                    component={NavLink}
                    to={AppRouteSpace([
                        AppRouteEnum.app,
                        AppRouteEnum.chargers,
                        String(data.id),
                        AppRouteEnum.configurations,
                    ])}
                    sx={deviceActionSx}
                    startIcon={<Settings/>}
                >
                    <FormattedMessage id="configurations"/>
                </Button>
            )}
            {canReadChargerLogs && (
                <Button
                    component={NavLink}
                    to={AppRouteSpace([
                        AppRouteEnum.app,
                        AppRouteEnum.chargers,
                        String(data.id),
                        AppRouteEnum.logs,
                    ])}
                    sx={deviceActionSx}
                    startIcon={<ListAlt/>}
                >
                    <FormattedMessage id="ocppLogsTitle"/>
                </Button>
            )}
        </>
    );
};
